import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useAccount } from '../account/store.ts';

/** Where the tour's screenshots are, beside the app (public/landing/, made by `pnpm shots`, kept out of the offline cache). */
const LANDING_FOLDER = '/landing/';

type Audience = 'Everyone' | 'Writers' | 'Game masters';

/** The tour: each tool on the sample realm, The Kingdom of Asteria (e2e/landing.shots.ts). */
const TOUR: readonly { shot: string; name: string; for: Audience; title: string; text: string; alt: string }[] = [
  {
    shot: 'entry',
    name: 'Entries',
    for: 'Everyone',
    title: 'Everything in your world gets an entry',
    text: 'People, places, factions, creatures, items, events and ideas, each with its own text, facts and pictures. Type [[ to link one entry to another, and say how they’re related: child of, lives in, ruler of. Every entry also shows what links to it.',
    alt: 'The entry for Arion Valen: his birth year, a short biography, his character sheet, and his relationships to other entries.',
  },
  {
    shot: 'graph',
    name: 'Knowledge graph',
    for: 'Everyone',
    title: 'See how it all connects',
    text: 'The knowledge graph draws your entries and their relationships as a web: who’s allied with whom, who’s whose child, what lies inside what. Filter it by type or collection, and spot the entries nothing links to yet.',
    alt: 'The knowledge graph of the sample realm: coloured circles for people, places and factions, joined by arrows.',
  },
  {
    shot: 'atlas',
    name: 'Atlas',
    for: 'Everyone',
    title: 'Maps that know your world',
    text: 'Upload a map of a world, a city or a dungeon, then add pins and regions linked to your entries. Measure distances on its own scale, lay a hex grid over it, and link maps together to go from the continent down to a single tavern.',
    alt: 'A painted map of the Northern Continent with a hex grid, pins for cities and battles, and a kingdom’s border drawn as a region.',
  },
  {
    shot: 'chronology',
    name: 'Chronology',
    for: 'Everyone',
    title: 'Your history, in order',
    text: 'Eras and events on timelines, with timelines inside timelines, so a war or a dynasty keeps its own history and can still be read with the rest. Each event links to the entries and places it involves.',
    alt: 'The Global Timeline: the First Age, with the Founding of Asteria and an omen seen before a battle, each linked to entries.',
  },
  {
    shot: 'calendar',
    name: 'Calendars',
    for: 'Everyone',
    title: 'Your world’s own calendar',
    text: 'Months of any length, weeks with their own day names, seasons, moons and festivals. Set it as the realm’s calendar, and every date in the Chronology is shown in it, with events on their days.',
    alt: 'A month of the Reckoning of Asteria, with its own day names, a new moon, and the Battle of Red Hollow on the 20th.',
  },
  {
    shot: 'board',
    name: 'Idea board',
    for: 'Writers',
    title: 'Think it through on a board',
    text: 'A corkboard for working out a plot or a villain: notes, cards, shapes and pictures you can place anywhere and join with arrows. Cards can link to your entries, maps and events.',
    alt: 'An idea board for a villain’s motivation: cards for the villain, his secret, the hero and the conflict, joined by lines.',
  },
  {
    shot: 'scene',
    name: 'Story studio',
    for: 'Writers',
    title: 'Write the story in the world it’s set in',
    text: 'Outline a novel in acts, chapters and scenes, with a word goal for each. Write each scene in a full editor with spelling and a thesaurus, open focus mode to see nothing else, and let Scan for Connections find the entries a scene mentions.',
    alt: 'A scene of the novel Blood and Silver being written, with the outline of acts and chapters beside it and a word-goal bar.',
  },
  {
    shot: 'consistency',
    name: 'Consistency check',
    for: 'Writers',
    title: 'Catch mistakes before your readers do',
    text: 'The consistency check reads your entries’ facts and dates and points out what can’t all be true: a child born before their parent, allies who are also enemies, a chain of command that goes round in a circle.',
    alt: 'The consistency check finding two contradictions: a child born before their parent, and two factions that are both allies and enemies.',
  },
  {
    shot: 'campaign',
    name: 'Campaigns',
    for: 'Game masters',
    title: 'Run the session from one screen',
    text: 'A campaign keeps your party, cast, sessions, encounters and quests. At the table, the Run screen tracks initiative, hit points and conditions, while the play log writes down what happens as you go.',
    alt: 'The Run screen mid-encounter: the party’s hit points, the initiative order in round 2, session notes and a play log.',
  },
  {
    shot: 'sheet',
    name: 'Character sheets',
    for: 'Game masters',
    title: 'Character sheets that do the maths',
    text: 'Sheets for player characters, NPCs and creatures, from a template for the game you play. Modifiers update as you type, and every roll on the sheet uses its own numbers.',
    alt: 'A fifth-edition character sheet for Arion Valen: class, level, background, and ability scores with their modifiers.',
  },
  {
    shot: 'tables',
    name: 'Random tables and dice',
    for: 'Game masters',
    title: 'Let the dice decide',
    text: 'Random tables for wandering monsters, weather, loot and rumours, with results that roll their own dice. The dice roller works next to anything you have open, from 1d20+5 to a pool of d10s counting successes.',
    alt: 'A table of wandering encounters with a rolled result, and the dice roller showing a history of three rolls.',
  },
  {
    shot: 'modes',
    name: 'Knowledge modes',
    for: 'Everyone',
    title: 'Explore it the way your job needs',
    text: 'Follow the links through your world in columns: pick a map, then a place on it, then its lore and its people. There are modes for an explorer, a writer, a game master, an architect and a researcher.',
    alt: 'Explorer mode: the Northern Continent, then the Ashwing Eyrie on it, then the Ashwing Drake that nests there.',
  },
];

const SIDES = [
  {
    id: 'writers',
    title: 'For writers',
    pitch: 'Your novel, written inside the world it’s set in.',
    features: [
      { name: 'Story studio', text: 'Outline in acts, chapters and scenes, with a word goal for the whole project.' },
      { name: 'Lore beside the page', text: 'Write a scene with the entries it involves right next to it.' },
      { name: 'Focus mode', text: 'Nothing but the scene on screen, and a log of every writing session.' },
      { name: 'Consistency check', text: 'Catches a child born before their parent, or an event dated outside its era.' },
      { name: 'Spelling and thesaurus', text: '“walked” offers “strode”. Both run in your browser and know your world’s names.' },
      { name: 'Copies for readers', text: 'Share the finished lore with readers: what they’re meant to read, and nothing more.' },
    ],
  },
  {
    id: 'game-masters',
    title: 'For game masters',
    pitch: 'Your campaign, run from the world it’s played in.',
    features: [
      { name: 'Campaigns', text: 'The party and cast, sessions with notes and a play log, encounters and quests.' },
      { name: 'Character sheets', text: 'Modifiers work themselves out as you type, and every 🎲 rolls with the sheet’s numbers.' },
      { name: 'Dice roller', text: '1d20+5, 4d6kh3, or a pool of ten-siders counting successes, beside whatever you’re doing.' },
      { name: 'Tables and generators', text: 'Wandering monsters, the weather, a tavern, or twenty villages in one go.' },
      { name: 'Player view', text: 'The map on the TV, under fog until you reveal it.' },
      { name: 'Copies for players', text: 'Your players get the world without your GM-only notes.' },
    ],
  },
] as const;

const PROMISES = [
  {
    title: 'Encrypted before it leaves your device',
    text: 'Your realms are encrypted with your own account key, so Realmery’s server can’t read them.',
  },
  {
    title: 'Works offline',
    text: 'Your realms are kept in your browser. Once you’ve signed in on a device, they open there with or without a connection, and they back up when you’re online again.',
  },
  {
    title: 'Yours to take away',
    text: 'Export a whole realm as a .realmery file for the desktop app, or as JSON, Markdown or HTML.',
  },
  {
    title: 'On the desktop too',
    text: 'Realmery for Windows opens the same realms, kept as files on your own computer.',
  },
] as const;

const ROLES = [
  { name: 'Co-owner', text: 'builds the whole world with you, taking turns to edit.' },
  { name: 'Player', text: 'gets a copy without your GM-only notes, or anything still under a map’s fog.' },
  { name: 'Reader', text: 'gets a copy of what you’ve written for readers, to read and not change.' },
] as const;

/**
 * Where signed-out visitors land: what Realmery is and does, with ways to sign up and
 * sign in. Every realm page needs an account, even on the free tier
 * (docs/ACCOUNTS-PLAN.md section 5); the account keeps each person's realms apart.
 */
export function WelcomePage() {
  const account = useAccount();
  const navigate = useNavigate();
  useEffect(() => {
    if (account.status === 'signed-in') void navigate({ to: '/', replace: true });
  }, [account.status, navigate]);
  const unavailable = account.status === 'unavailable';

  return (
    <div className="landing">
      <section className="landing-hero" aria-labelledby="landing-title">
        <div className="landing-sparks" aria-hidden="true">
          {Array.from({ length: 14 }, (_, i) => (
            <span key={i} />
          ))}
        </div>
        <div className="landing-hero-text">
          <p className="landing-kicker">Realmery</p>
          <h1 id="landing-title">Where worlds are made.</h1>
          <p className="landing-lede">
            Your world’s people, places, maps, history and stories in one place, all linked to each other. For writers
            building a setting and game masters running one. It’s encrypted on your own device, and yours to keep.
          </p>
          {unavailable ? (
            <p className="callout landing-offline" role="status">
              Realmery can't be reached right now. Once you have signed in on this device, your realms open here even when
              you are offline.
            </p>
          ) : (
            <>
              <div className="actions landing-actions">
                <Link className="button primary" to="/signup">
                  Create an account
                </Link>
                <Link className="button landing-ghost" to="/signin">
                  Sign in
                </Link>
              </div>
              <p className="landing-small">Free. Invite-only while Realmery is being tested.</p>
            </>
          )}
        </div>
        <RealmCollage />
      </section>

      <section className="landing-section" aria-labelledby="landing-tour">
        <p className="eyebrow">A tour of the sample realm</p>
        <h2 id="landing-tour">One world, everything linked</h2>
        <p className="landing-intro">
          These pictures are from The Kingdom of Asteria, the sample realm you can add to your account to try everything out.
          Every tool works on the same entries, so a change in one place shows up everywhere else.
        </p>
        <ol className="landing-tour">
          {TOUR.map((stop) => (
            <li key={stop.shot} className="landing-stop">
              <a className="landing-shot" href={`${LANDING_FOLDER}${stop.shot}.jpg`} target="_blank" rel="noopener">
                <img src={`${LANDING_FOLDER}${stop.shot}.jpg`} alt={stop.alt} width="1280" height="800" loading="lazy" decoding="async" />
              </a>
              <div className="landing-stop-text">
                <p className="landing-stop-name">
                  {stop.name} <span className={`landing-for landing-for-${stop.for.replace(' ', '-').toLowerCase()}`}>{stop.for}</span>
                </p>
                <h3>{stop.title}</h3>
                <p>{stop.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-section" aria-labelledby="landing-crafts">
        <p className="eyebrow">Two crafts, one world</p>
        <h2 id="landing-crafts">Write in it. Play in it.</h2>
        <div className="landing-sides">
          {SIDES.map((side) => (
            <article key={side.id} className={`landing-side landing-side-${side.id}`} aria-labelledby={`side-${side.id}`}>
              <h3 id={`side-${side.id}`}>{side.title}</h3>
              <p className="landing-side-pitch">{side.pitch}</p>
              <ul>
                {side.features.map((feature) => (
                  <li key={feature.name}>
                    <strong>{feature.name}</strong>
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section landing-share" aria-labelledby="landing-sharing">
        <div>
          <p className="eyebrow">Share the right parts</p>
          <h2 id="landing-sharing">Not everyone gets the whole world</h2>
          <p>
            Choose who each entry is for. When you invite someone, their role decides what they get. They get your changes
            when you choose to send them, and the server can’t read any of it.
          </p>
        </div>
        <dl className="landing-roles">
          {ROLES.map((role) => (
            <div key={role.name}>
              <dt>{role.name}</dt>
              <dd>{role.text}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="landing-section" aria-labelledby="landing-promises">
        <p className="eyebrow">Your world stays yours</p>
        <h2 id="landing-promises">Private by design</h2>
        <div className="landing-promises">
          {PROMISES.map((promise) => (
            <div key={promise.title} className="landing-promise">
              <h3>{promise.title}</h3>
              <p>{promise.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-cta" aria-labelledby="landing-start">
        <h2 id="landing-start">Start your first realm</h2>
        <p>Begin with an empty realm, or explore the sample realm, The Kingdom of Asteria, first.</p>
        {!unavailable && (
          <div className="actions landing-actions">
            <Link className="button primary" to="/signup">
              Create an account
            </Link>
            <Link className="button landing-ghost" to="/signin">
              Sign in
            </Link>
          </div>
        )}
        <p className="landing-small">
          <Link to="/quickstart">Read the Quickstart Guide</Link> · <Link to="/download">Download for Windows</Link>
        </p>
      </section>
    </div>
  );
}

/** A glimpse of one realm through several of its tools at once: its graph, its sharing, a fight, its history and a check. Decoration only. */
function RealmCollage() {
  return (
    <div className="landing-collage" aria-hidden="true">
      <div className="collage-card collage-graph">
        <p className="collage-tool">Knowledge graph</p>
        <svg viewBox="0 0 260 150">
          <g className="graph-edges">
            <line x1="130" y1="75" x2="60" y2="38" />
            <line x1="130" y1="75" x2="205" y2="32" />
            <line x1="130" y1="75" x2="72" y2="118" />
            <line x1="130" y1="75" x2="196" y2="112" />
            <line x1="205" y1="32" x2="196" y2="112" />
            <line x1="60" y1="38" x2="22" y2="80" />
            <line x1="196" y1="112" x2="244" y2="84" />
            <line x1="72" y1="118" x2="130" y2="140" />
          </g>
          <circle className="node node-hub" cx="130" cy="75" r="11" />
          <circle className="node node-place" cx="60" cy="38" r="7" />
          <circle className="node node-faction" cx="205" cy="32" r="8" />
          <circle className="node node-event" cx="72" cy="118" r="6" />
          <circle className="node node-person" cx="196" cy="112" r="7" />
          <circle className="node node-person" cx="22" cy="80" r="5" />
          <circle className="node node-creature" cx="244" cy="84" r="5" />
          <circle className="node node-place" cx="130" cy="140" r="5" />
          <text x="130" y="100">Asteria</text>
          <text x="205" y="16">Drax Empire</text>
          <text x="196" y="134">Arion Valen</text>
        </svg>
      </div>
      <div className="collage-card collage-share">
        <p className="collage-tool">Shared with</p>
        <ul>
          <li>
            <i className="avatar avatar-a">M</i> Mara <b className="role-co">Co-owner</b>
          </li>
          <li>
            <i className="avatar avatar-b">J</i> Jonah <b>Player</b>
          </li>
          <li>
            <i className="avatar avatar-c">R</i> Rin <b>Reader</b>
          </li>
        </ul>
      </div>
      <div className="collage-card collage-fight">
        <p className="collage-tool">Run · Round 2</p>
        <ul>
          <li>
            <span className="init">18</span> Arion Valen
            <span className="hp">
              <span style={{ width: '82%' }} />
            </span>
          </li>
          <li className="turn">
            <span className="init">16</span> Drax War Hound
            <span className="hp">
              <span style={{ width: '45%' }} />
            </span>
          </li>
          <li>
            <span className="init">12</span> Seneschal Bram
            <span className="hp">
              <span style={{ width: '100%' }} />
            </span>
          </li>
        </ul>
      </div>
      <div className="collage-card collage-time">
        <p className="collage-tool">Chronology</p>
        <div className="collage-eras">
          <span>Age of Ash</span>
          <span>The Sundering</span>
        </div>
        <div className="collage-line">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      <div className="collage-card collage-check">
        <p className="collage-tool">Consistency check</p>
        <p className="collage-warn">Child born before parent</p>
        <p className="collage-meta">Elena Valen · Arion Valen</p>
      </div>
    </div>
  );
}
